import * as React from 'react';
import { AvatarColour, User, SubscriptionStatus } from '../../models';
import { CategoryFavourites, SetupCardFunction } from '../../stores';
import { validateFullName } from '../../validators/name';
import { validatePasswordWithConfirm } from '../../validators/password';
import { LoadingSpinner } from '../Common/LoadingSpinner';
import { Button, ButtonStyle } from '../Forms/Button';
import { FormRow } from '../Forms/FormRow';
import Checkout from '../Premium/Checkout';
import { PaymentForm } from '../Premium/Checkout/PaymentForm';
import AnimateHeight from '../Utils/AnimateHeight';
import { AvatarEditor } from './AvatarEditor';
import { CancelPremiumDialog } from './CancelPremiumDialog';
import { FavouritesList } from './FavouritesList';
import { UserProfileFormRow } from './UserProfileFormRow';
import { UserProfileFormError } from './UserProfileFormRow/UserProfileFormError';
import './style.css';

export interface UserProfileProps {
  cancelMembershipError?: Error;
  cardUpdateError?: Error;
  cardUpdateRequestRunning?: boolean;
  error: boolean;
  favouritesByCategory: Array<CategoryFavourites>;
  loading: boolean;
  onCancelPremium: () => void;
  onRequestPremium: () => void;
  onSaveProfile: (
    name: string,
    colour: AvatarColour,
    password?: string,
    currentPassword?: string
  ) => void;
  onUpdateCard: (setupCard: SetupCardFunction) => Promise<void>;
  user: User;
}

export interface UserProfileState {
  cancellingPremium?: boolean;
  colour: AvatarColour;
  confirmPassword: string;
  currentPassword: string;
  name: string;
  nameError?: string;
  password: string;
  passwordError?: string;
  updatingCard?: boolean;
}

export class UserProfile extends React.Component<
  UserProfileProps,
  UserProfileState
> {
  private paymentFormRef = React.createRef<{
    getWrappedInstance(): PaymentForm | undefined;
  }>();

  constructor(props: UserProfileProps) {
    super(props);

    this.state = {
      colour: props.user.colour || AvatarColour.Red,
      confirmPassword: '',
      currentPassword: '',
      name: `${props.user.firstName || ''} ${props.user.lastName || ''}`.trim(),
      password: ''
    };
  }

  render() {
    const { error, favouritesByCategory, loading, user } = this.props;
    const {
      cancellingPremium,
      colour,
      confirmPassword,
      currentPassword,
      name,
      nameError,
      password,
      passwordError,
      updatingCard
    } = this.state;

    return (
      <main className="user-profile">
        <h1 className="user-profile-heading">Your User Profile</h1>
        <form className="user-profile-form" onSubmit={this.trySaveProfile}>
          <div className="user-profile-inner">
            <div className="user-profile-avatar-wrapper">
              <AvatarEditor
                colour={colour}
                firstName={user.firstName}
                lastName={user.lastName}
                onUpdate={(newColour: AvatarColour) =>
                  this.setState({
                    colour: newColour
                  })
                }
              />
            </div>
            <div className="user-profile-form-wrapper">
              <UserProfileFormRow
                className="user-profile-form-row"
                label="Email"
                text={user.email}
              />

              <UserProfileFormRow
                className="user-profile-form-row"
                inputClassName="user-profile-input"
                label="Display Name"
                autoComplete="name"
                autoFocus={true}
                value={name}
                error={nameError}
                onChange={event =>
                  this.updateName((event.target as HTMLInputElement).value)
                }
              />

              {this.getPasswordFields(
                confirmPassword,
                currentPassword,
                password,
                passwordError
              )}

              {this.getMembershipSection()}

              <UserProfileFormError
                className="edge-of-play-form-row user-profile-error"
                error={
                  error
                    ? `Profile update failed, please check your details and try again`
                    : undefined
                }
              />
            </div>
          </div>

          <AnimateHeight height={updatingCard ? 0 : 'auto'}>
            <FormRow>
              <Button
                className="save-profile-button"
                type="submit"
                disabled={loading}
              >
                Save Changes
              </Button>
            </FormRow>
          </AnimateHeight>
        </form>

        {this.getCardUpdateForm()}

        <FavouritesList favouritesByCategory={favouritesByCategory} />

        {cancellingPremium ? (
          <CancelPremiumDialog
            onAccept={this.cancelPremium}
            onClose={() => this.setState({ cancellingPremium: false })}
          />
        ) : null}

        {loading ? (
          <div className="user-profile-loader-wrapper">
            <LoadingSpinner className="user-profile-loader" />
          </div>
        ) : null}
      </main>
    );
  }

  private getPasswordFields = (
    confirmPassword: string,
    currentPassword: string,
    password: string,
    passwordError?: string
  ): Array<JSX.Element> => {
    const fields = [
      <UserProfileFormRow
        key="new-password"
        className="user-profile-form-row"
        inputClassName="user-profile-input"
        label="Change Password"
        autoComplete="new-password"
        type="password"
        value={password}
        error={passwordError}
        onChange={event =>
          this.updatePassword((event.target as HTMLInputElement).value)
        }
      />
    ];

    if (!password) {
      return fields;
    }

    fields.push(
      <UserProfileFormRow
        key="new-password-confirm"
        className="user-profile-form-row"
        inputClassName="user-profile-input"
        label="Confirm Password"
        type="password"
        value={confirmPassword}
        onChange={event =>
          this.updateConfirmPassword((event.target as HTMLInputElement).value)
        }
      />
    );

    fields.push(
      <UserProfileFormRow
        key="current-password"
        className="user-profile-form-row"
        inputClassName="user-profile-input"
        label="Current Password"
        type="password"
        value={currentPassword}
        onChange={event =>
          this.updateCurrentPassword((event.target as HTMLInputElement).value)
        }
      />
    );

    return fields;
  };

  private shouldSubmitBeDisabled = (): boolean => {
    const { loading } = this.props;
    const {
      confirmPassword,
      name,
      nameError,
      password,
      passwordError
    } = this.state;

    return !!(
      loading ||
      !name ||
      (!password && confirmPassword) ||
      (!confirmPassword && password) ||
      nameError ||
      passwordError
    );
  };

  private updateName = (name: string): void => {
    this.validateName(name);
    this.setState({
      name
    });
  };

  private updatePassword = (password: string): void => {
    this.validatePassword(password);
    this.setState({
      password
    });
  };

  private updateConfirmPassword = (confirmPassword: string): void => {
    this.validatePassword(undefined, confirmPassword);
    this.setState({
      confirmPassword
    });
  };

  private updateCurrentPassword = (currentPassword: string): void => {
    this.validatePassword(undefined, undefined, currentPassword);
    this.setState({
      currentPassword
    });
  };

  private validateName = (name: string): void => {
    this.setState({
      nameError: validateFullName(name)
    });
  };

  private validatePassword = (
    password: string = this.state.password,
    confirmPassword: string = this.state.confirmPassword,
    currentPassword: string = this.state.currentPassword
  ): void => {
    this.setState({
      passwordError: this.getPasswordError(
        password,
        confirmPassword,
        currentPassword
      )
    });
  };

  private trySaveProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    const { onSaveProfile } = this.props;
    const {
      colour,
      confirmPassword,
      currentPassword,
      name,
      password
    } = this.state;

    event.preventDefault();

    // Force validation before we check if we can submit
    this.validateName(name);
    this.validatePassword(password, confirmPassword);

    if (onSaveProfile && !this.shouldSubmitBeDisabled()) {
      await onSaveProfile(
        name as string,
        colour as AvatarColour,
        password as string,
        currentPassword as string
      );

      if (!this.props.error) {
        // Clear local state if update succeeds (no external error passed back)
        this.setState({
          confirmPassword: '',
          currentPassword: '',
          password: ''
        });
      }
    }
  };

  private getPasswordError = (
    password?: string,
    confirmPassword?: string,
    currentPassword?: string
  ): string | undefined => {
    if (!password) {
      return undefined;
    }

    const newPasswordValidation = validatePasswordWithConfirm(
      password,
      confirmPassword
    );

    if (newPasswordValidation) {
      return newPasswordValidation;
    }

    return !currentPassword ? `Current password must be supplied` : undefined;
  };

  private getMembershipSection = () => {
    return (
      <>
        <UserProfileFormRow
          className="user-profile__membership-row"
          inputClassName="user-profile__membership-content"
          label="Membership"
        >
          <span className="user-profile__membership-status">
            {this.getMembershipStatusText()}
          </span>

          {this.getMembershipButtons()}
        </UserProfileFormRow>
        {this.getCancelMembershipError()}
      </>
    );
  };

  private getMembershipStatusText = () => {
    const { user } = this.props;

    if (user.isPremium) {
      if (user.premiumEndsAt) {
        const dateFormatter = new Intl.DateTimeFormat();

        return `Premier (Cancelled) - Expires ${dateFormatter.format(
          user.premiumEndsAt
        )}`;
      }

      return `Premier`;
    }

    return `Standard`;
  };

  private getMembershipButtons = () => {
    const { onRequestPremium, user } = this.props;
    const { updatingCard } = this.state;

    if (user.isActiveCancelledPremium) {
      return null;
    }

    switch (user.premium) {
      case SubscriptionStatus.Active:
        return (
          <>
            <Button
              type="button"
              buttonStyle={updatingCard ? ButtonStyle.Danger : undefined}
              className="user-profile__membership-button user-profile__update-card-button"
              onClick={this.toggleUpdateCard}
            >
              {updatingCard ? `Cancel card update` : `Update card`}
            </Button>
            {updatingCard ? null : (
              <Button
                type="button"
                buttonStyle={ButtonStyle.Danger}
                className="user-profile__membership-button user-profile__cancel-premium-button"
                onClick={() => this.setState({ cancellingPremium: true })}
              >
                Cancel membership
              </Button>
            )}
          </>
        );
      default:
        return (
          <Button
            type="button"
            className="user-profile__membership-button user-profile__upgrade-to-premium"
            onClick={onRequestPremium}
          >
            Upgrade to premier
          </Button>
        );
    }
  };

  private getCardUpdateForm = () => {
    const {
      onUpdateCard,
      cardUpdateError,
      cardUpdateRequestRunning
    } = this.props;
    const { updatingCard } = this.state;

    return (
      <AnimateHeight height={updatingCard ? 'auto' : 0}>
        <div>
          <Checkout
            className="user-profile__update-card-form"
            forwardRef={this.paymentFormRef}
            onSubmit={async setupCard => {
              try {
                await onUpdateCard(setupCard);

                // Hide card update form after successful update
                this.toggleUpdateCard();

                // And clear the card form if we can
                if (this.paymentFormRef.current) {
                  const paymentForm = this.paymentFormRef.current.getWrappedInstance();

                  if (paymentForm) {
                    paymentForm.clearInputs();
                  }
                }
              } catch (ex) {
                // Ignore errors - it gets handled by the error flag in the store
              }
            }}
            paymentError={cardUpdateError}
            paymentRequestRunning={cardUpdateRequestRunning}
            submitButtonText="Update card"
          />
        </div>
      </AnimateHeight>
    );
  };

  private toggleUpdateCard = () => {
    const { updatingCard } = this.state;

    this.setState({
      updatingCard: !updatingCard
    });

    if (this.paymentFormRef.current) {
      const paymentForm = this.paymentFormRef.current.getWrappedInstance();

      if (paymentForm) {
        paymentForm.clearInputs();
      }
    }
  };

  private cancelPremium = () => {
    this.setState({ cancellingPremium: false });

    this.props.onCancelPremium();
  };

  private getCancelMembershipError = () => {
    const { cancelMembershipError } = this.props;

    if (!cancelMembershipError) {
      return null;
    }

    return (
      <div className="user-profile__cancel-premium-error">
        <p>
          There was an error while trying to cancel your membership. Please
          refresh the page and try again.
        </p>
        <p>
          If the issue persists, please contact us at{' '}
          <a className="edge-of-play-link" href="mailto:admin@edgeofplay.com">
            admin@edgeofplay.com
          </a>{' '}
          for support.
        </p>
      </div>
    );
  };
}



// WEBPACK FOOTER //
// ./src/app/components/UserProfile/index.tsx